<template>
  <v-container fluid>
    <v-card>
      <v-toolbar
        color="indigo"
        dark>

        <v-toolbar-title>
          <v-icon left>{{ toolbarIcon }}</v-icon>
          {{ $vuetify.lang.t('$vuetify.carParks.title') }}
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <tooltip-basic :icon="carParkAddIcon"
                       :text="$vuetify.lang.t(`$vuetify.carParks.carParkIcons.${ComponentMode.CREATE}`)"
                       emit-state="open-create-dialog"
                       @open-create-dialog="openCreateCarParkDialog">
        </tooltip-basic>

        <v-progress-linear :active="isLoading" indeterminate absolute bottom>
        </v-progress-linear>
      </v-toolbar>

      <v-card-text>
        <v-tabs v-model="tab" center-active grow :show-arrows="$vuetify.breakpoint.smAndDown">
          <v-tab v-for="entry in parks" :key="entry.id">
            <v-img v-if="$vuetify.breakpoint.smAndUp" :src="entry.image" max-height="100"
                   max-width="150" contain class="mx-md-8"></v-img>
            {{ entry.name }}
          </v-tab>
          <v-tabs-items v-model="tab">
            <v-tab-item v-for="entry in parks" :key="`tab-${entry.id}`">
              <v-card flat>
                <v-card-text class="py-0 my-0">
                  <car-park-drivers :car-park-id="entry.id"></car-park-drivers>
                </v-card-text>
                <v-card-actions class="py-1 my-0">
                  <v-spacer></v-spacer>
                  <tooltip-basic :icon="carParkUpdateIcon"
                                 :text="$vuetify.lang.t(`$vuetify.carParks.carParkIcons.${ComponentMode.EDIT}`)"
                                 emit-state="open-edit-dialog"
                                 @open-edit-dialog="openEditCarParkDialog(entry)">
                  </tooltip-basic>
                  <tooltip-basic :icon="carParkRemoveIcon"
                                 :text="$vuetify.lang.t(`$vuetify.carParks.carParkIcons.${ComponentMode.REMOVE}`)"
                                 emit-state="open-remove-dialog"
                                 @open-remove-dialog="openRemoveCarParkDialog(entry)">
                  </tooltip-basic>
                </v-card-actions>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-tabs>
      </v-card-text>
    </v-card>

    <car-park-entry
      :dialog="carParkDialog"
      :value="carParkEntry"
      :title="carParkDialogTitle"
      @close-car-park-dialog="carParkDialog = false"
      @process-car-park="editCarPark"></car-park-entry>

    <confirmation-dialog :dialog="carParkRemoveDialog"
                         :title="$vuetify.lang.t(`$vuetify.carParks.removeCarParkDialog.title`)"
                         :text="$vuetify.lang.t(`$vuetify.carParks.removeCarParkDialog.text`)"
                         :reject="$vuetify.lang.t('$vuetify.map.criticalPopup.allCameraCorrectDialog.reject')"
                         :confirm="$vuetify.lang.t('$vuetify.map.criticalPopup.allCameraCorrectDialog.confirm')"
                         @confirmation-dialog-closed="carParkRemoveDialog = false"
                         @confirmation-dialog-confirmed="removeCarPark">
    </confirmation-dialog>

  </v-container>

</template>

<script>
import backend from '@/backend'
import { CarPark } from '@/models/carParks/carPark'
import CarParkDrivers from '@/components/map/carPark/carParkDrivers'
import CarParkEntry from '@/components/map/carPark/carParkEntry'
import TooltipBasic from '@/components/utils/tooltipBasic'
import { mdiMinusBox, mdiPlusBox, mdiSquareEditOutline, mdiTrainCar } from '@mdi/js'
import { ComponentMode } from '@/enum'
import snackbarMixin from '@/mixins/snackbarMixin'
import ConfirmationDialog from '@/components/utils/confirmationDialog'

export default {
  name: 'CarParkPermissions',
  components: { ConfirmationDialog, TooltipBasic, CarParkEntry, CarParkDrivers },
  mixins: [snackbarMixin],
  data: () => ({
    parks: [],
    tab: null,
    isLoading: false,

    carParkDialog: false,
    carParkRemoveDialog: false,
    carParkDialogMode: ComponentMode.CREATE,
    carParkEntry: new CarPark(),

    toolbarIcon: mdiTrainCar,
    carParkAddIcon: mdiPlusBox,
    carParkUpdateIcon: mdiSquareEditOutline,
    carParkRemoveIcon: mdiMinusBox
  }),
  computed: {
    carParkDialogTitle () {
      return `$vuetify.carParks.carParkForm.titles.${this.carParkDialogMode}`
    }
  },
  methods: {
    openCreateCarParkDialog () {
      this.carParkEntry = new CarPark()
      this.carParkDialogMode = ComponentMode.CREATE
      this.carParkDialog = true
    },
    openEditCarParkDialog (entry) {
      this.carParkEntry = new CarPark({ ...entry })
      this.carParkDialogMode = ComponentMode.EDIT
      this.carParkDialog = true
    },
    openRemoveCarParkDialog (entry) {
      this.carParkEntry = new CarPark({ ...entry })
      this.carParkRemoveDialog = true
    },
    async getCarParks () {
      this.isLoading = true
      try {
        const result = await backend.getCarParks()
        this.parks = result.data.map(item => new CarPark(item))
      } catch (e) {
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },
    async editCarPark () {
      this.isLoading = true
      try {
        await backend.editCarPark(this.carParkEntry.backendModel())
        await this.getCarParks()
        this.openSuccessSnackbar(`$vuetify.carParks.carParkForm.success.${this.carParkDialogMode}`)
        this.carParkDialog = false
      } catch (e) {
        this.processFailedRequest(e)
      } finally {
        this.isLoading = false
      }
    },
    async removeCarPark () {
      this.isLoading = true
      try {
        await backend.removeCarPark(this.carParkEntry.backendModel())
        await this.getCarParks()
        this.openSuccessSnackbar(`$vuetify.carParks.carParkForm.success.${ComponentMode.REMOVE}`)
        this.carParkDialog = false
      } catch (e) {
        this.processFailedRequest(e)
      } finally {
        this.isLoading = false

      }
    }
  },
  async created () {
    this['ComponentMode'] = ComponentMode
    await this.getCarParks()
  }
}
</script>

<style scoped>

</style>
